import cx from 'classnames';
import { type CSSProperties, type HTMLAttributes, type ReactNode, type Ref } from 'react';
import CancelCloseOutline from '../../icons/CancelCloseOutline.svg?react';
import CheckCircleFill from '../../icons/CheckCircleFill.svg?react';
import ExclamationMarkFill from '../../icons/ExclamationMarkFill.svg?react';
import InfoCircleFill from '../../icons/InfoCircleFill.svg?react';
import WarningFill from '../../icons/WarningFill.svg?react';
import styles from './Toast.module.css';

// ─── Types ─────────────────────────────────────────────────────────────────

type ToastVariant = 'info' | 'success' | 'warning' | 'danger';

type ToastProps = {
  /** Required test identifier */
  dataTestId: string;
  /** Semantic variant — sets background, border, icon, and accent color */
  variant?: ToastVariant;
  /** Main content: text, Trans components, or JSX */
  message: ReactNode;
  /** Secondary line under the message — the message turns semibold when set */
  description?: ReactNode;
  /** Custom icon override — defaults to a built-in icon per variant */
  icon?: ReactNode;
  /** Right-side action slot (buttons, links) */
  action?: ReactNode;
  /** Shows a close button on the right */
  isClosable?: boolean;
  /** Called when the close button is clicked */
  onClose?: () => void;
  /** Shows a bar depleting over `duration` */
  showProgress?: boolean;
  /** Seconds the progress bar takes to deplete. Only read when `showProgress` is set */
  duration?: number;
  /** Overrides the default 384px width */
  width?: number;
  /** Additional CSS class (Tailwind, layout overrides) */
  className?: string;
  /** Forwarded to the root div element */
  ref?: Ref<HTMLDivElement>;
} & Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'children' | 'style'>;

export type { ToastProps, ToastVariant };

// ─── Helpers ───────────────────────────────────────────────────────────────

const DEFAULT_TOAST_DURATION = 4.5;

export { DEFAULT_TOAST_DURATION };

const variantClass: Record<ToastVariant, string> = {
  info: styles.variantInfo,
  success: styles.variantSuccess,
  warning: styles.variantWarning,
  danger: styles.variantDanger,
};

const defaultIcon: Record<ToastVariant, ReactNode> = {
  info: <InfoCircleFill width="24" height="24" aria-hidden="true" />,
  success: <CheckCircleFill width="24" height="24" aria-hidden="true" />,
  warning: <WarningFill width="24" height="24" aria-hidden="true" />,
  danger: <ExclamationMarkFill width="24" height="24" aria-hidden="true" />,
};

function hasContent(node: ReactNode) {
  return node !== undefined && node !== null && node !== false;
}

// ─── Component ─────────────────────────────────────────────────────────────

/**
 * The presentational toast card. It renders no live region of its own — inside
 * a `Toaster` the announcement is Radix's job, and announcing twice would make
 * a screen reader read every toast twice.
 *
 * Application code should not render this directly: call `toast.error({…})` and
 * let the `Toaster` mounted at the app root render it.
 */
export function Toast(props: ToastProps) {
  const {
    dataTestId,
    variant = 'info',
    message,
    description,
    icon,
    action,
    isClosable = true,
    onClose,
    showProgress = false,
    duration = DEFAULT_TOAST_DURATION,
    width,
    className,
    ref,
    ...rest
  } = props;

  const withDescription = hasContent(description);
  const renderedIcon = icon !== undefined ? icon : defaultIcon[variant];

  // Width and progress speed are per-toast values, so they cannot live in the
  // stylesheet. They are passed as custom properties rather than as real inline
  // styles, which keeps Toast.module.css the only place that decides how they
  // are used — and keeps them overridable by a consumer class.
  const cssVariables = {
    ...(width !== undefined && { '--toast-width': `${width}px` }),
    ...(showProgress && { '--toast-progress-duration': `${duration}s` }),
  } as CSSProperties;

  const combinedClassName = cx(
    styles.toast,
    variantClass[variant],
    withDescription && styles.withDescription,
    className,
  );

  return (
    <div
      ref={ref}
      className={combinedClassName}
      style={cssVariables}
      data-testid={dataTestId}
      data-variant={variant}
      {...rest}
    >
      <span className={styles.iconSlot}>{renderedIcon}</span>
      <div className={styles.body}>
        <div className={styles.message}>{message}</div>
        {withDescription && <div className={styles.description}>{description}</div>}
      </div>
      {action && <div className={styles.actions}>{action}</div>}
      {isClosable && (
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          data-testid={`${dataTestId}-close`}
          aria-label="Close"
        >
          <CancelCloseOutline width={12} height={12} aria-hidden="true" />
        </button>
      )}
      {showProgress && duration > 0 && <span className={styles.progress} aria-hidden="true" />}
    </div>
  );
}
